/* eslint-disable react-hooks/exhaustive-deps */
// TOPO "Clean central" (modelo_topo = 5): composição limpa e sem hardcode por tenant.
// Estrutura:
//  - faixa fina superior: telefone/WhatsApp (contatos da REGIÃO ativa via useSiteRegiao,
//    igual ao Topo padrão) + redes sociais;
//  - logo CENTRALIZADA (maior);
//  - menu horizontal ABAIXO da logo (mesmos itens do menu do site) + hambúrguer no mobile,
//    que reusa o DrawerComponent (o mesmo do Topo padrão, via setActiveDrawer);
//  - ao rolar (sticky) vira uma barra compacta: logo pequena à esquerda + menu, fundo
//    cor1 SÓLIDO e texto adaptativo.
// O default export é um ErrorBoundary com fallback pro Topo padrão (mesmo padrão do WelcomeIa):
// qualquer erro de dados/render degrada pro cabeçalho de sempre — nunca deixa a página sem topo.
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FaFacebook, FaInstagram, FaLinkedin, FaPhoneAlt, FaTiktok, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { ensureReadableTextColor, isColorLight, MaskPhone } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { contatosTopoCentralDireita } from '../../../utils/topoCentralContatosDireita';
import { ehTopoSoHamburguer } from '../../../utils/topoSoHamburguer';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useSiteRegiao } from '../../context/Info';
import { temSeletorIdiomas } from '../../../utils/idiomaMoedaSite';
import { IdiomaNavItem } from '../idiomas/menu';
import Topo from '../topo/Topo';
import styles from './TopoCentral.module.css';

interface ITopoCentral {
    site: any;
    setActiveDrawer: any;
    fallback?: React.ReactNode;
}

type NavItem = { label: string; href: string; external?: boolean };

const buildWhatsHref = (site: any, phone?: string) => {
    const clean = (phone || '').replace(/\D/g, '');
    if (!clean) return '';
    const text =
        site?.texto_botao_whats ||
        `Olá, estou visitando o site da ${site?.Dados?.empresa || ''}, gostaria de um atendimento personalizado. Pode me ajudar?`;
    return `https://api.whatsapp.com/send?phone=55${clean}&text=${encodeURIComponent(text)}`;
};

const TopoCentralInner: React.FC<ITopoCentral> = ({ site: siteProp, setActiveDrawer }) => {
    // MULTI-REGIÃO: contatos (tel1/tel2/email1) trocados pelos da região ativa quando houver.
    const site = useSiteRegiao(siteProp);
    const { allDataHome } = useAuth();
    const router = useRouter();
    const codsite = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;

    // codsite só resolve no cliente (appendCodsiteQuery usa o host) — anexa só após montar
    // p/ o href do SSR bater com a 1ª render do cliente (evita hydration mismatch).
    const [isMounted, setIsMounted] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        setIsMounted(true);
        const onScroll = () => setScrolled((window.scrollY || 0) > 80);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    const withCodsite = (path: string) => (isMounted ? appendCodsiteQuery(path, codsite) : path);

    // faixa fina superior: cor2 (destaque) com fallback cor1 + texto adaptativo real
    const corBarra = site?.cor2 || site?.cor1 || '#1f1f1f';
    const corBarraText = ensureReadableTextColor(isColorLight(corBarra) ? site?.cor11 : site?.cor13, corBarra) || '#ffffff';
    // área da logo/menu: cor1 (fundo do topo) + texto adaptativo
    const corHeader = site?.cor1 || '#ffffff';
    const corHeaderText = ensureReadableTextColor(isColorLight(corHeader) ? site?.cor11 : site?.cor13, corHeader) || '#111111';
    const logoHeight = Number(site?.tamanho_logo) > 1 ? Number(site?.tamanho_logo) : 96;

    // #1473 (cs2641): tenant que pediu o topo sem nenhum link de texto, só o hambúrguer.
    const soHamburguer = ehTopoSoHamburguer(site);

    // menu horizontal: mesmos itens do menu do site (transações + menus disponíveis + blog).
    const navItems: NavItem[] = [];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        const label = low === 'venda' ? 'Comprar' : low === 'aluguel' ? 'Alugar' : low === 'temporada' ? 'Temporada' : t;
        navItems.push({ label, href: `/imoveis/${low}/-/-/-/-` });
    });
    (allDataHome?.menus || [])
        .filter((m: any) => m?.Disponivel && m?.titulo)
        .slice(0, 6)
        .forEach((m: any) => {
            navItems.push({ label: m.titulo, href: m?.link ? m.link : `/menu/${m.titulo}`, external: Boolean(m?.link && !String(m.link).startsWith('/')) });
        });
    if (allDataHome?.blogs?.[0]?.id) navItems.push({ label: 'Blog', href: '/blog' });

    const socials = [
        { href: site?.Dados?.instagram, Icon: FaInstagram, rede: 'Instagram' },
        { href: site?.Dados?.facebook, Icon: FaFacebook, rede: 'Facebook' },
        { href: site?.Dados?.youtube, Icon: FaYoutube, rede: 'Youtube' },
        { href: site?.Dados?.tiktok, Icon: FaTiktok, rede: 'Tiktok' },
        { href: site?.Dados?.Linkedin, Icon: FaLinkedin, rede: 'Linkedin' },
    ].filter((s) => s.href);

    // cs2641: WhatsApps fixos das sócias no canto DIREITO da faixa (no lugar do tel1 à esquerda).
    const contatosDireita = contatosTopoCentralDireita(site);
    const phones = contatosDireita
        ? contatosDireita.map((c) => ({ tel: c.tel, nome: c.nome, whats: true }))
        : [
              { tel: site?.Dados?.tel1, nome: site?.Dados?.tel1_nome, whats: site?.Dados?.tel1_whats },
              { tel: site?.Dados?.tel2, nome: site?.Dados?.tel2_nome, whats: site?.Dados?.tel2_whats },
          ].filter((p) => p.tel);

    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';

    const renderMenu = (compact: boolean) => (
        <nav className={compact ? styles.menuCompact : styles.menu} aria-label="Menu principal">
            {navItems.map((item, idx) =>
                item.external ? (
                    <a
                        key={idx}
                        href={item.href}
                        className={styles.menuLink}
                        style={{ color: compact ? corHeaderText : corHeaderText }}
                        rel="noopener noreferrer"
                    >
                        {item.label}
                    </a>
                ) : (
                    <Link prefetch={false} key={idx} href={withCodsite(item.href)} className={styles.menuLink} style={{ color: corHeaderText }}>
                        {item.label}
                    </Link>
                )
            )}
            {/* 22/10: idioma no computador, no fim do menu (até 900px a linha está na gaveta) */}
            {temSeletorIdiomas(site) ? <IdiomaNavItem ocultarAte={900} classeBotao={styles.menuLink} estilo={{ color: corHeaderText }} /> : null}
        </nav>
    );

    // 22/10 (rodada 2): idioma NA BARRA também no celular (a linha da gaveta saiu). No computador ele fica no
    // menu de texto (acima); fora dele — no celular, ao lado do ☰, e no topo "só ☰" (cs2641) em qualquer
    // largura — entra este, só a bandeira até 900px.
    const temIdioma = temSeletorIdiomas(site);
    const idiomaBarra = (estilo?: React.CSSProperties) =>
        temIdioma ? (
            <IdiomaNavItem compactoAte={900} ocultarAcima={soHamburguer ? undefined : 900} estilo={{ color: corHeaderText, ...estilo }} />
        ) : null;

    const hamburger = (
        <button type="button" className={`${styles.hamburger}${soHamburguer ? ' ' + styles.sempreVisivel : ''}`} aria-label="Abrir menu" onClick={() => setActiveDrawer(true)}>
            <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true" style={{ color: corHeaderText }}>
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
        </button>
    );

    // cs2641: no desktop os WhatsApps saem da faixa fina e ficam na linha da logo, colados à
    // esquerda do ☰ (e na barra fixa ao rolar). No celular seguem na faixa (não cabem ao lado da logo).
    const contatosAoLadoMenu = contatosDireita ? (
        <div className={styles.contatosAoLadoMenu}>
            {contatosDireita.map((c, idx) => (
                <a
                    key={idx}
                    className={styles.contatoAoLadoMenu}
                    href={buildWhatsHref(site, c.tel)}
                    style={{ color: corHeaderText }}
                    rel="noopener noreferrer"
                    target="_blank"
                    title={`WhatsApp de ${c.nome}`}
                    onClick={() => trackMetaEvent(site, 'Contact', { content_name: 'Contato do cabeçalho', contact_method: 'whatsapp', content_category: 'header' })}
                >
                    <FaWhatsapp aria-hidden="true" />
                    <span className={styles.topContactLinha}>
                        {MaskPhone(c.tel)}
                        <em className={styles.topContactNomeLinha}>{c.nome}</em>
                    </span>
                </a>
            ))}
        </div>
    ) : null;

    return (
        // BUILDER: âncora estável do bloco "topo" (cobre esta variante em qualquer página).
        <div className={styles.wrapper} data-builder-section="topo">
            {/* faixa fina superior */}
            <div className={`${styles.topBar}${contatosDireita && socials.length === 0 ? ' ' + styles.topBarSoCelular : ''}`} style={{ background: corBarra, color: corBarraText }}>
                <div className={`${styles.topBarInner}${contatosDireita ? ' ' + styles.topBarDireita : ''}`}>
                    <div className={`${styles.topContacts}${contatosDireita ? ' ' + styles.soCelular : ''}`}>
                        {phones.map((p, idx) => {
                            const href = p.whats ? buildWhatsHref(site, p.tel) : `tel:+55${(p.tel || '').replace(/\D/g, '')}`;
                            return (
                                <a
                                    key={idx}
                                    className={styles.topContact}
                                    href={href}
                                    style={{ color: corBarraText }}
                                    rel="noopener noreferrer"
                                    target={p.whats ? '_blank' : undefined}
                                    title={contatosDireita ? `WhatsApp de ${p.nome}` : p.nome || (p.whats ? 'WhatsApp' : 'Telefone')}
                                    onClick={() => trackMetaEvent(site, 'Contact', { content_name: 'Contato do cabeçalho', contact_method: 'whatsapp', content_category: 'header' })}
                                >
                                    {p.whats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                    {contatosDireita ? (
                                        <span className={styles.topContactLinha}>
                                            {MaskPhone(p.tel)}
                                            <em className={styles.topContactNomeLinha}>{p.nome}</em>
                                        </span>
                                    ) : (
                                        <span className={styles.topContactText}>
                                            {MaskPhone(p.tel)}
                                            {p.nome ? <em className={styles.topContactName}>{p.nome}</em> : null}
                                        </span>
                                    )}
                                </a>
                            );
                        })}
                    </div>
                    {socials.length > 0 ? (
                        <div className={styles.topSocials}>
                            {socials.map((s, idx) => (
                                <a
                                    key={idx}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`Navegar para o ${s.rede} da imobiliária`}
                                    className={styles.topSocial}
                                    style={{ color: corBarraText }}
                                >
                                    <s.Icon aria-hidden="true" />
                                </a>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* área central: logo grande + menu abaixo */}
            <div className={`${styles.main}${contatosDireita ? ' ' + styles.mainContatos : ''}`} style={{ background: corHeader }}>
                <div className={`${styles.logoRow}${contatosDireita ? ' ' + styles.logoRowContatos : ''}${temIdioma ? ' ' + styles.logoRowIdioma : ''}`}>
                    <div className={`${styles.hamburgerSlot}${soHamburguer ? ' ' + styles.sempreVisivel : ''}`} aria-hidden={soHamburguer ? 'true' : undefined}>
                        {soHamburguer ? null : hamburger}
                    </div>
                    <Link prefetch={false} href={withCodsite('/')} onClick={() => trackMetaEvent(site, 'ViewContent', { content_name: 'Logo da imobiliária', content_category: 'header_navigation' })} className={styles.logoLink} title={`Imobiliária ${site?.Dados?.empresa || ''}`}>
                        {logoSrc ? (
                            <Image
                                src={logoSrc}
                                alt={`Imobiliária ${site?.Dados?.empresa || ''}`}
                                title={`Imobiliária ${site?.Dados?.empresa || ''}`}
                                className={styles.logo}
                                height={logoHeight}
                                width={360}
                                priority
                                fetchPriority="high"
                                unoptimized
                            />
                        ) : (
                            <span className={styles.logoText} style={{ color: corHeaderText }}>
                                {site?.Dados?.empresa || ''}
                            </span>
                        )}
                    </Link>
                    <div className={`${styles.hamburgerSlot}${soHamburguer ? ' ' + styles.sempreVisivel + ' ' + styles.hamburgerSlotDireita : ''}`} aria-hidden={soHamburguer || temIdioma ? undefined : 'true'}>
                        {contatosAoLadoMenu}
                        {idiomaBarra()}
                        {soHamburguer ? hamburger : null}
                    </div>
                </div>
                {soHamburguer ? null : <div className={styles.menuRow}>{renderMenu(false)}</div>}
            </div>

            {/* barra compacta ao rolar (sticky) */}
            <div
                className={`${styles.stickyBar} ${scrolled ? styles.stickyBarVisible : ''}`}
                style={{ background: corHeader }}
                aria-hidden={!scrolled}
            >
                <div className={`${styles.stickyInner}${contatosDireita ? ' ' + styles.stickyInnerContatos : ''}`}>
                    <Link prefetch={false} href={withCodsite('/')} onClick={() => trackMetaEvent(site, 'ViewContent', { content_name: 'Logo da imobiliária', content_category: 'header_navigation' })} className={styles.stickyLogoLink} title={`Imobiliária ${site?.Dados?.empresa || ''}`}>
                        {logoSrc ? (
                            <Image src={logoSrc} alt={`Imobiliária ${site?.Dados?.empresa || ''}`} className={styles.stickyLogo} height={44} width={160} unoptimized />
                        ) : (
                            <span className={styles.logoText} style={{ color: corHeaderText, fontSize: 18 }}>
                                {site?.Dados?.empresa || ''}
                            </span>
                        )}
                    </Link>
                    {soHamburguer ? <div className={styles.stickyMenu} /> : <div className={styles.stickyMenu}>{renderMenu(true)}</div>}
                    {contatosAoLadoMenu}
                    {idiomaBarra({ marginLeft: 'auto' })}
                    <div className={`${styles.stickyHamburger}${soHamburguer ? ' ' + styles.sempreVisivel : ''}`}>{hamburger}</div>
                </div>
            </div>
        </div>
    );
};

// ErrorBoundary → fallback pro Topo padrão (mesmo padrão do WelcomeIa).
class TopoCentral extends React.Component<ITopoCentral, { hasError: boolean }> {
    constructor(props: ITopoCentral) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: o topo degrada pro padrão sem quebrar a página.
    }
    render() {
        const { site, setActiveDrawer, fallback } = this.props;
        const defaultTopo = fallback ?? <Topo site={site} setActiveDrawer={setActiveDrawer} />;
        if (this.state.hasError || !site) return defaultTopo;
        return <TopoCentralInner {...this.props} />;
    }
}

export default TopoCentral;
